import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    Link,
    useTheme,
    alpha,
    Grid,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Person,
    Lock,
    Login as LoginIcon,
    Shield,
    Speed,
    Cloud,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';

const Login: React.FC = observer(() => {
    const { authStore } = useStore();
    const theme = useTheme();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username || !password) {
            return;
        }

        const success = await authStore.login({ username, password });
        if (success) {
            // Перенаправление будет выполнено автоматически через Navigate
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    if (authStore.isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
        <Box sx={{ textAlign: 'center', p: 2 }}>
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                }}
            >
                {icon}
            </Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {description}
            </Typography>
        </Box>
    );

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${theme.palette.primary.main.substring(1)}' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                },
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={4} alignItems="center" sx={{ minHeight: '100vh' }}>
                    {/* Левая панель с информацией */}
                    <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Box sx={{ pr: 4 }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 800,
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    mb: 2,
                                }}
                            >
                                WH Service
                            </Typography>
                            <Typography variant="h5" color="text.secondary" paragraph>
                                Система управления заявками нового поколения
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                                Автоматизируйте обработку заявок, интегрируйтесь с CRM и коммуникационными платформами,
                                отслеживайте статистику в реальном времени.
                            </Typography>

                            <Grid container spacing={3} sx={{ mt: 4 }}>
                                <Grid item xs={12} sm={4}>
                                    <FeatureCard
                                        icon={<Speed sx={{ fontSize: 32, color: 'primary.main' }} />}
                                        title="Быстро"
                                        description="Мгновенная обработка заявок"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FeatureCard
                                        icon={<Shield sx={{ fontSize: 32, color: 'primary.main' }} />}
                                        title="Безопасно"
                                        description="Защита данных на всех уровнях"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FeatureCard
                                        icon={<Cloud sx={{ fontSize: 32, color: 'primary.main' }} />}
                                        title="Облачно"
                                        description="Доступ из любой точки мира"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    {/* Правая панель с формой */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                maxWidth: 480,
                                mx: 'auto',
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    width: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {/* Логотип и заголовок */}
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2,
                                        }}
                                    >
                                        <LoginIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                                    </Box>
                                    <Typography variant="h4" fontWeight={700} gutterBottom>
                                        Вход в систему
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Введите ваши учетные данные для доступа
                                    </Typography>
                                </Box>

                                {authStore.error && (
                                    <Alert
                                        severity="error"
                                        sx={{
                                            mb: 3,
                                            borderRadius: 2,
                                        }}
                                    >
                                        {authStore.error}
                                    </Alert>
                                )}

                                <Box component="form" onSubmit={handleSubmit}>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="username"
                                        label="Имя пользователя"
                                        name="username"
                                        autoComplete="username"
                                        autoFocus
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        disabled={authStore.loading}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person sx={{ color: 'text.secondary' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            },
                                        }}
                                    />
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        name="password"
                                        label="Пароль"
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={authStore.loading}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock sx={{ color: 'text.secondary' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            },
                                        }}
                                    />

                                    <Box sx={{ mt: 2, mb: 2, textAlign: 'right' }}>
                                        <Link
                                            href="#"
                                            variant="body2"
                                            sx={{
                                                textDecoration: 'none',
                                                color: 'primary.main',
                                                fontWeight: 500,
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            Забыли пароль?
                                        </Link>
                                    </Box>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        disabled={authStore.loading || !username || !password}
                                        sx={{
                                            mt: 3,
                                            mb: 2,
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            background: authStore.loading
                                                ? 'grey.300'
                                                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                            boxShadow: authStore.loading ? 'none' : '0 4px 12px rgba(25, 118, 210, 0.25)',
                                            '&:hover': {
                                                background: authStore.loading
                                                    ? 'grey.300'
                                                    : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                                boxShadow: authStore.loading ? 'none' : '0 6px 20px rgba(25, 118, 210, 0.35)',
                                            },
                                        }}
                                    >
                                        {authStore.loading ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                                                Вход...
                                            </>
                                        ) : (
                                            <>
                                                <LoginIcon sx={{ mr: 1 }} />
                                                Войти в систему
                                            </>
                                        )}
                                    </Button>
                                </Box>


                            </Paper>

                            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                                © 2025 Все права защищены.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
});

export default Login;